import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, TrendingUp, TrendingDown, Flame } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import ExportButton from '../../../components/ui/ExportButton';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';
import { getBackendBaseUrl } from '../../../lib/backendUrl';

const API = `${getBackendBaseUrl()}/api/india`;

function formatMcap(val) {
  if (!val) return '—';
  const cr = val / 1e7;
  if (cr >= 100000) return `₹${(cr / 100000).toFixed(1)}L Cr`;
  if (cr >= 1000) return `₹${(cr / 1000).toFixed(1)}K Cr`;
  return `₹${cr.toFixed(0)} Cr`;
}

function ChangeBadge({ value, suffix = '%' }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const positive = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}{value.toFixed(2)}{suffix}
    </span>
  );
}

function MoverCard({ item, rank, onClick }) {
  const positive = item.change_pct > 0;
  return (
    <button onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:border-emerald-500/30 hover:bg-muted/30 transition-all text-left w-full">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
        positive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-500'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{item.company_name}</span>
          <span className="text-[10px] text-muted-foreground">{item.symbol}</span>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-muted-foreground">{item.sector || '—'}</span>
          <span className="text-[10px] text-muted-foreground">{formatMcap(item.market_cap)}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold">₹{item.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        <ChangeBadge value={item.change_pct} />
      </div>
    </button>
  );
}

export default function TopMoversPage() {
  const navigate = useNavigate();
  const [direction, setDirection] = useState('gainers');
  const [minMcap, setMinMcap] = useState(100);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asOf, setAsOf] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/top-movers?direction=${direction}&limit=50&min_mcap=${minMcap}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setAsOf(data.as_of);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [direction, minMcap]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const MCAP_FILTERS = [
    { key: 0, label: 'All' },
    { key: 100, label: '100+ Cr' },
    { key: 1000, label: '1,000+ Cr' },
    { key: 5000, label: '5,000+ Cr' },
    { key: 20000, label: '20,000+ Cr' },
  ];

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Flame className="w-5 h-5 text-emerald-500" /> Top Movers
        </h1>
        <p className="text-xs text-muted-foreground">
          Daily top gainers and losers across the market
          {asOf && <span className="ml-1">· as of {asOf}</span>}
        </p>
      </div>

      {/* Direction tabs */}
      <div className="flex gap-0 border-b border-border">
        {[
          { key: 'gainers', label: 'Top Gainers', icon: TrendingUp },
          { key: 'losers', label: 'Top Losers', icon: TrendingDown },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setDirection(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              direction === tab.key
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Market cap filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-muted-foreground">Market Cap:</span>
        <div className="flex gap-1">
          {MCAP_FILTERS.map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={minMcap === f.key ? 'default' : 'outline'}
              className={`text-xs h-7 ${minMcap === f.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setMinMcap(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} stocks</span>
        <ExportButton rows={items} filename={`top-${direction}`} />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
          <p className="text-xs text-muted-foreground">Fetching live market data — first load may take up to 60 seconds...</p>
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <Flame className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No movers found for this filter.</p>
          <p className="text-xs text-muted-foreground mt-1">Try selecting "All" market cap filter, or wait for market data to refresh.</p>
        </div>
      ) : (
        <>
          {/* Card view for top 5 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {items.slice(0, 6).map((item, i) => (
              <MoverCard
                key={item.isin}
                item={item}
                rank={i + 1}
                onClick={() => item.symbol && navigate(`/public/india-company/${item.symbol}`)}
              />
            ))}
          </div>

          {/* Table for the rest */}
          {items.length > 6 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-emerald-800 text-white">
                    <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">#</th>
                    <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                    <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Sector</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Price</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Prev Close</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Change</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Volume</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Market Cap</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(6).map((r, i) => (
                    <tr key={r.isin || r.symbol} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                      onClick={() => r.symbol && navigate(`/public/india-company/${r.symbol}`)}>
                      <td className="py-2 px-3 text-muted-foreground">{i + 7}</td>
                      <td className="py-2 px-3">
                        <span className="font-medium">{r.company_name || r.symbol}</span>
                        {r.company_name && <span className="text-muted-foreground ml-1.5">{r.symbol}</span>}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{r.sector || '—'}</td>
                      <td className="py-2 px-3 text-right font-medium">₹{r.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td className="py-2 px-3 text-right text-muted-foreground">₹{r.prev_close?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '—'}</td>
                      <td className="py-2 px-3 text-right"><ChangeBadge value={r.change_pct} /></td>
                      <td className="py-2 px-3 text-right text-muted-foreground">{r.volume ? (r.volume / 1e5).toFixed(1) + 'L' : '—'}</td>
                      <td className="py-2 px-3 text-right text-muted-foreground">{formatMcap(r.market_cap)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
